// Deferred Pardot iframes (templates/_blocks/_contentMatrix/_formEmbedBlock.twig renders [data-form-embed] placeholders).
// Each form is created client-side with attribution params on its src (Attribution.js), so cached pages stay generic.
// Loads early enough that nobody waits: at once if near the first screen or linked by #hash, ~2 screens ahead
// while scrolling, on intent (pointerdown/focus on a link to it), and in any case once the page goes idle after load.
// window.wghi.formEmbed.prefill(placeholder, values) (re)loads a form with visible fields filled in from Pardot query
// params ({first_name: 'Ann', …}); the WebMCP form tools use it (src/js/WebMcp.js). It never submits.
// Visitors who arrive with values (the form start block's sessionStorage handoff from FormStart.js, or ?email=,
// ?first_name=… links from campaigns) get the first form loaded straight away with those fields filled in (ADR 0031).
// After a successful submit, the form's Pardot Thank You Code posts a `wghi:lead` message with the prospect's email and
// phone; this pushes `lead_submission` with `enhanced_conversion_data` for Google Ads, then goes to the thank-you page
// (ADR 0036, docs/tracking.md). The values never reach a URL.
import iframeResize from 'iframe-resizer/js/iframeResizer';

(function() {
    // Pardot field names differ from the utm_* keys (field IDs on info.wegrowhairindy.com forms: source, gclid,
    // UTM_Campaign, UTM_Medium). Both spellings are sent; Pardot ignores params without a matching field.
    var ALIASES = {
        utm_source: ['source'],
        utm_medium: ['UTM_Medium'],
        utm_campaign: ['UTM_Campaign'],
        utm_term: ['UTM_Term'],
        utm_content: ['UTM_Content'],
    };
    var STAGGER_MS = 300;
    var PREFILL_KEY = 'wghi_prefill';
    var PREFILL_PARAMS = ['email', 'first_name', 'last_name', 'phone'];
    var PREFILL_TTL_MS = 30 * 60 * 1000;
    var LEAD_REDIRECT_MS = 2000;

    function clean(value) {
        return String(value).replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, 200);
    }

    // Values handed over by the form start block (single use) plus allowlisted URL params, which are then removed
    // from the address bar so they aren't bookmarked, shared or sent on in a referrer.
    function takePrefill() {
        var values = {};
        try {
            var stored = JSON.parse(window.sessionStorage.getItem(PREFILL_KEY));
            window.sessionStorage.removeItem(PREFILL_KEY);
            if ( stored && stored.email && Date.now() - stored.ts < PREFILL_TTL_MS ) {
                values.email = clean(stored.email);
            }
        } catch (e) {}
        var search = new URLSearchParams(location.search);
        var found = false;
        PREFILL_PARAMS.forEach(function(name) {
            if ( search.has(name) ) {
                found = true;
                if ( clean(search.get(name)) ) values[name] = clean(search.get(name));
                search.delete(name);
            }
        });
        if ( found ) {
            var query = search.toString();
            history.replaceState(history.state, '', location.pathname + (query ? '?' + query : '') + location.hash);
        }
        return values;
    }

    function buildSrc(src, values) {
        var url = new URL(src, location.href);
        Object.keys(values || {}).forEach(function(name) {
            if ( values[name] != null && values[name] !== '' ) {
                url.searchParams.set(name, values[name]);
            }
        });
        var attribution = window.wghi && window.wghi.attribution ? window.wghi.attribution.get() : {};
        Object.keys(attribution).forEach(function(key) {
            var value = attribution[key];
            [key].concat(ALIASES[key] || []).forEach(function(name) {
                if ( value && !url.searchParams.has(name) ) {
                    url.searchParams.set(name, value);
                }
            });
        });
        return url.toString();
    }

    function load(placeholder, trigger, values) {
        if ( placeholder.hasAttribute('data-form-loaded') ) {
            return;
        }
        placeholder.setAttribute('data-form-loaded', trigger);

        var src = buildSrc(placeholder.getAttribute('data-src'), values);
        var iframe = document.createElement('iframe');
        iframe.src = src;
        iframe.title = placeholder.getAttribute('data-title') || 'Contact form';
        iframe.width = '100%';
        iframe.height = placeholder.getAttribute('data-height') || '700';
        iframe.setAttribute('scrolling', 'no');
        iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
        iframe.addEventListener('load', function() {
            placeholder.classList.add('is-loaded');
            iframeResize({ checkOrigin: [new URL(src).origin], warningTimeout: 0 }, iframe);
        }, { once: true });
        placeholder.appendChild(iframe);

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'form_embed_loaded', formEmbedTrigger: trigger });
    }

    function init() {
        var placeholders = Array.prototype.slice.call(document.querySelectorAll('[data-form-embed]'));
        if ( !placeholders.length ) {
            return;
        }
        var pending = function() { return placeholders.filter(function(p) { return !p.hasAttribute('data-form-loaded'); }); };

        // Arrived with an email/name: load the first form now with it filled in, and bring it into view.
        var prefill = takePrefill();
        if ( Object.keys(prefill).length ) {
            load(placeholders[0], 'prefill', prefill);
            placeholders[0].scrollIntoView({ block: 'center' });
        }

        // Linked by #hash: load now.
        var target = location.hash.length > 1 ? document.getElementById(decodeURIComponent(location.hash.slice(1))) : null;
        placeholders.forEach(function(placeholder) {
            if ( target && (target === placeholder || target.contains(placeholder) || placeholder.contains(target)) ) {
                load(placeholder, 'initial');
            }
        });

        // Near the first screen (observer's first report): load now. Scrolling: start about two screens ahead.
        // Positions come from the observer, not getBoundingClientRect(), so startup doesn't force a layout.
        var firstReport = true;
        var observer = 'IntersectionObserver' in window ? new IntersectionObserver(function(entries) {
            var initial = firstReport;
            firstReport = false;
            entries.forEach(function(entry) {
                if ( entry.isIntersecting ) {
                    observer.unobserve(entry.target);
                    load(entry.target, initial && entry.boundingClientRect.top < window.innerHeight * 1.5 ? 'initial' : 'viewport');
                }
            });
        }, { rootMargin: '0px 0px 200% 0px' }) : null;
        pending().forEach(function(placeholder) {
            observer ? observer.observe(placeholder) : load(placeholder, 'fallback');
        });

        // Intent: a CTA pointing at a form (or the block around it).
        var onIntent = function(event) {
            var link = event.target.closest && event.target.closest('a[href*="#"]');
            var id = link ? link.hash.slice(1) : '';
            var anchor = id ? document.getElementById(decodeURIComponent(id)) : null;
            if ( !anchor ) {
                return;
            }
            pending().forEach(function(placeholder) {
                if ( anchor === placeholder || anchor.contains(placeholder) || placeholder.contains(anchor) ) {
                    load(placeholder, 'intent');
                }
            });
        };
        document.addEventListener('pointerdown', onIntent, { passive: true });
        document.addEventListener('focusin', onIntent);

        // Idle after load: everything else, staggered, so forms are ready before anyone scrolls to them.
        var idle = function() {
            pending().forEach(function(placeholder, index) {
                setTimeout(function() {
                    if ( observer ) observer.unobserve(placeholder);
                    load(placeholder, 'idle');
                }, index * STAGGER_MS);
            });
        };
        var afterLoad = function() {
            'requestIdleCallback' in window ? window.requestIdleCallback(idle, { timeout: 2000 }) : setTimeout(idle, 1000);
        };
        document.readyState === 'complete' ? afterLoad() : window.addEventListener('load', afterLoad, { once: true });
    }

    // Google wants E.164 (+13175551234). US numbers arrive as typed: 10 digits, or 11 starting with 1.
    function e164(phone) {
        var digits = String(phone || '').replace(/\D/g, '');
        if ( /^\s*\+/.test(phone) && digits.length >= 11 && digits.length <= 15 ) return '+' + digits;
        if ( digits.length === 10 ) return '+1' + digits;
        if ( digits.length === 11 && digits.charAt(0) === '1' ) return '+' + digits;
        return '';
    }

    // Only messages from one of our form iframes, at that iframe's origin, and only the first one per page.
    var leadHandled = false;
    function onLead(event) {
        var data = event.data;
        if ( leadHandled || !data || data.type !== 'wghi:lead' ) {
            return;
        }
        var placeholder = null;
        Array.prototype.forEach.call(document.querySelectorAll('[data-form-embed] iframe'), function(iframe) {
            if ( iframe.contentWindow === event.source && new URL(iframe.src).origin === event.origin ) {
                placeholder = iframe.closest('[data-form-embed]');
            }
        });
        if ( !placeholder ) {
            return;
        }
        leadHandled = true;

        var email = clean(data.email || '').toLowerCase();
        var userData = {};
        if ( /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) ) userData.email = email;
        if ( e164(data.phone) ) userData.phone_number = e164(data.phone);

        // Same-origin thank-you pages only; otherwise the Thank You Code's own fallback redirect takes over.
        var target = null;
        try {
            target = new URL(data.redirect, location.href);
            if ( target.origin !== location.origin ) target = null;
        } catch (e) {}
        var gone = false;
        var go = function() {
            if ( !gone && target ) {
                gone = true;
                location.href = target.href;
            }
        };

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'lead_submission',
            leadForm: placeholder.getAttribute('data-form-name') || '',
            // The Pardot form's own label from its Thank You Code; several Craft forms can share one Pardot form.
            leadFormId: clean(data.form_name || ''),
            enhanced_conversion_data: userData,
            eventCallback: go,
            eventTimeout: LEAD_REDIRECT_MS,
        });
        // GTM only honours eventTimeout once it has loaded; this covers a blocked or slow container.
        setTimeout(go, LEAD_REDIRECT_MS + 500);
    }

    function prefill(placeholder, values) {
        var iframe = placeholder.querySelector('iframe');
        if ( iframe ) {
            iframe.src = buildSrc(placeholder.getAttribute('data-src'), values);
        } else {
            load(placeholder, 'agent', values);
        }
        placeholder.scrollIntoView({ block: 'center' });
    }

    window.wghi = window.wghi || {};
    window.wghi.formEmbed = { prefill: prefill };

    window.addEventListener('message', onLead);

    document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
})();
